import { generateHash } from 'crm-index-visualization-core/core/model/hash';
import { createLegacyTableConfig, createLegacyDataWellConfig } from './legacy-adapters';
import { datesHash } from '../util/storageMangager';
const VIZ_CONFIG_STORAGE_PREFIX = 'vizconf';
/**
 * Outer shell for visualization configuration that handles versioning and storage.
 * The inner config schema is owned by the viz team.
 * Each viz type can have its own version.
 */
export class VizConfig {
  /**
   * Create a new VizConfig
   * @param vizType The name of the visualization type (i.e. 'table', 'report')
   * @param version The version of the visualization type (i.e. '0', '1' , '2', '3-SOME_GATE')
   * @param innerConfig The inner configuration of the visualization type
   */
  constructor(vizType, version, innerConfig) {
    this.metadata = {
      vizType,
      version
    };
    this.innerConfig = innerConfig;
  }

  /**
   * Creates a new VizConfig with updated properties
   * @param updates Partial updates to apply to the config
   * @returns A new VizConfig instance with the updates applied
   */
  with(updates) {
    var _updates$vizType, _updates$version;
    const updatedConfig = updates.config ? Object.assign({}, this.innerConfig, updates.config) : this.innerConfig;
    return new VizConfig((_updates$vizType = updates.vizType) !== null && _updates$vizType !== void 0 ? _updates$vizType : this.metadata.vizType, (_updates$version = updates.version) !== null && _updates$version !== void 0 ? _updates$version : this.metadata.version, updatedConfig);
  }

  /**
   * Get the client cache key for this config
   */
  static getClientCacheKey(hash) {
    return `${VIZ_CONFIG_STORAGE_PREFIX}-${hash}`;
  }

  /**
   * Get the client cache key for this instance
   */
  async getClientCacheKey() {
    return VizConfig.getClientCacheKey(await this.hash());
  }

  /**
   * Generate a hash for this config
   */
  hash() {
    return generateHash({
      metadata: this.metadata,
      innerConfig: this.innerConfig
    });
  }

  /**
   * Extract a VizConfig from a BE view for a specific visualization type
   * Note: This is for internal use. You won't need this as a visualization library owner.
   *
   * This method provides special bridge-code to extract table and report configs from BE views.
   * To use those special handling, specify vizType as 'table' with version '0', 'report' with '0', or 'datawell' with '0'.
   * Version '0' is code for legacy.
   *
   * @param view The BE view to extract config from
   * @param vizType The type of visualization to extract config for
   * @param version The version of the visualization
   * @returns A VizConfig instance with the extracted configuration
   */
  static fromBEView(view, vizType, version) {
    // For v0, handle special cases
    if (version === '0') {
      switch (vizType) {
        case 'table':
          return createLegacyTableConfig(view);
        case 'datawell':
          return createLegacyDataWellConfig(view);
        case 'report':
          return new VizConfig(vizType, version, {});
        default:
          // For v1+, we'll extract from view.state
          // For now, return empty config
          return new VizConfig(vizType, version, {});
      }
    }
    // TODO: Handle non-legacy configs
    return new VizConfig(vizType, version, {});
  }

  /**
   * Load a VizConfig from client cache
   * @throws if config cannot be loaded
   */
  static async fromClientCache(hash, clientStorage) {
    const key = VizConfig.getClientCacheKey(hash);
    const stored = await clientStorage.getItem(key);
    if (!stored) {
      throw new Error(`No viz config found for hash: ${hash}`);
    }
    const parsed = JSON.parse(stored);
    const {
      metadata,
      innerConfig
    } = parsed;
    return new VizConfig(metadata.vizType, metadata.version, innerConfig);
  }

  /**
   * Save this config to client cache
   * @returns hash that can be used to retrieve the config later
   */
  async toClientCache(clientStorage) {
    const serialized = {
      metadata: this.metadata,
      innerConfig: this.innerConfig
    };
    const hash = await this.hash();
    const key = await this.getClientCacheKey();
    await Promise.all([clientStorage.setItem(key, JSON.stringify(serialized)), datesHash(key, Date.now(), clientStorage)]);
    return hash;
  }

  /**
   * Get the inner configuration owned by the viz team
   */
  get config() {
    return this.innerConfig;
  }

  /**
   * Get the visualization type
   */
  get vizType() {
    return this.metadata.vizType;
  }

  /**
   * Get the version of this config
   */
  get version() {
    return this.metadata.version;
  }

  /**
   * Check if this config is compatible with the given version
   */
  isCompatibleWith(version) {
    return this.version === version;
  }
}

/**
 * Base interface for all visualization configurations
 */